// What this console remembers, and where.
//
// `progression.ts` owns no storage and no clock on purpose, so this file
// is the other half of the ladder: the record of what an operation has
// actually done, kept in `localStorage`, fed to `rungEvidence` with a
// `now` this app supplies. Reload the page and the rungs are where you
// left them, because the evidence for them is still on disk.
//
// **Everything here is on the operator's own machine.** There is no
// project, no bucket and no account behind this app; a fleet that closes
// the tab has the state, and a fleet that clears the browser has lost
// it. That is stated in the interface rather than hidden, along with the
// part that matters more: `localStorage` is not a keystore. A phone puts
// a device key in the Keystore or the Secure Enclave and a browser
// cannot, which §20.9 says out loud and this app repeats.

import type { ProfileId, Rung, StopFieldId } from "@wayfind/core";

/**
 * Bumped rather than migrated. A record shape that changed meaning — a
 * job's holder used to be inferred from `driverless`, which the ladder
 * then read as evidence — is worse to half-migrate than to drop, and
 * nothing has shipped that anyone is keeping.
 */
export const STORAGE_KEY = "wayfind.fleet.v3";

/** One place on a plan, with whatever fields this profile exposes. */
export interface StoredStop {
  id: string;
  label: string;
  lat: number;
  lon: number;
  /** Only ever holds keys the profile lists. An absent field does not exist. */
  fields: Partial<Record<StopFieldId, string | number | boolean>>;
}

export interface StoredJob {
  id: string;
  createdUnixSeconds: number;
  /** What the operation was when this went out. Profiles can change. */
  profile: ProfileId;
  stops: StoredStop[];
  /**
   * The identity of the **stop set**, which is what "the same round three
   * days running" is a comparison of. Deliberately not `planRef`: a
   * restaurant sending the same five addresses with fresh order numbers
   * every evening produces different plan bytes and the same round.
   */
  planKey: string;
  planRefHex: string;
  jobIdHex: string;
  /** The device it was sealed to, when it went to somebody's phone. */
  deviceId: string | null;
  /** The driver's name as typed, before there were devices to name. */
  driverName: string | null;
  /**
   * **A fact about the moment it was created**, and never edited
   * afterwards: there was nobody free to take it. Rung 4's evidence is
   * "a job was created with no driver available, twice", so awarding it
   * later must not erase the thing that made the rung necessary.
   */
  driverless: boolean;
  /**
   * Who is holding it *now* — a device id, `"self"` for this console, or
   * null for nobody. This is the live half, and it is the one that moves
   * when a job is awarded or handed on.
   */
  heldBy: string | null;
  mode: number;
  travelMode: number;
  notAfter: number;
  /** The customer's 78 bytes, as a URL into the consumer app. */
  trackUrl: string;
  /** `wayfind://ticket#…` — ciphertext, sealed to the device above. */
  driverUrl: string;
  sealedB64: string;
  optimized: boolean;
  totalMeters: number | null;
  startedUnixSeconds: number | null;
  finishedUnixSeconds: number | null;
  usedRungs: Rung[];
  routeId: string | null;
  serviceDay: number | null;
  offerId: string | null;
}

export interface StoredDevice {
  id: string;
  name: string;
  publicKeyHex: string;
  fingerprint: string;
  enrolledUnixSeconds: number;
  retiredUnixSeconds: number | null;
  /**
   * Present only for a device this tab stood in for. A real driver's
   * phone holds its own private key and this console never sees it; the
   * demonstration path is the one exception and is labelled as one.
   */
  standInPrivateKeyHex: string | null;
}

/**
 * One service day, which is **one artifact** (§21.11).
 *
 * There is no certificate field here and that absence is the point: the
 * day's PMTC rides inside the sealed ticket's signed preimage, so there
 * is no second value to store, to lose, or to hand over in the wrong
 * order. What is kept is the sealed bytes and the window they are good
 * for.
 */
export interface StoredRouteDay {
  serviceDay: number;
  /** The certificate's window — ≤48 h — which is what bounds the run. */
  notBefore: number;
  notAfter: number;
  deviceId: string | null;
  sealedB64: string;
  /** `wayfind://ticket#…` — the same bytes, as the QR and the file carry them. */
  driverUrl: string;
  mintedUnixSeconds: number;
}

export interface StoredRoute {
  id: string;
  name: string;
  createdUnixSeconds: number;
  /** §21: the root never leaves the depot. This is the depot. */
  rootSeedHex: string;
  rootPublicKeyHex: string;
  planKey: string;
  planRefHex: string;
  planB64: string;
  stops: StoredStop[];
  mode: number;
  travelMode: number;
  termFromUnixSeconds: number;
  termToUnixSeconds: number;
  /** One v2 link, minted once, good for the whole term. */
  parentUrl: string;
  days: StoredRouteDay[];
}

export interface StoredOffer {
  id: string;
  createdUnixSeconds: number;
  jobIdHex: string;
  url: string;
  planB64: string;
  planKey: string;
  planRefHex: string;
  stops: StoredStop[];
  notAfter: number;
  mode: number;
  stopCount: number;
  centroidLat: number;
  centroidLon: number;
  spread: number;
  spreadLabel: string;
  totalMeters: number;
  payMinor: number | null;
  currency: string | null;
  label: string | null;
  /** Cards that came back. Enrolment is the only bidding channel there is. */
  bidDeviceIds: string[];
  awardedDeviceId: string | null;
  awardedJobId: string | null;
  /** What `awardMatchesOffer` said, in its own words. */
  awardCheck: string | null;
}

export interface StoredSeed {
  label: string;
  addresses: string[];
  enrolledUnixSeconds: number;
  /** Whether customer links carry the `?b=` hint. Per-link, and off by default. */
  hintCustomerLinks: boolean;
}

/**
 * One offer this console has already made. Re-offering is gated on the
 * *occurrence count* rather than on a clock: an offer that was waved away
 * comes back the next time the need genuinely recurs, and never on a
 * timer.
 */
export interface Acknowledgement {
  occurrences: number;
  atUnixSeconds: number;
  /**
   * Set when the operator *took the offer up* rather than waving it
   * away. Taking up an unlock is the end of the conversation — the rung
   * is open and its concepts are on screen, and saying so twice is the
   * nagging DESIGN spends a whole section refusing to do. Waving one
   * away leaves this unset, and the offer comes back the next time the
   * need genuinely recurs.
   */
  settled?: boolean;
}

export interface FleetState {
  version: 3;
  /** Null until the operator has said what kind of operation this is. */
  profile: ProfileId | null;
  /** The dispatcher's long-lived signing identity. It only ever signs. */
  issuerSeedHex: string;
  /** This console as an enrolable device, so it can seal a job to itself. */
  consoleDevice: { privateKeyHex: string; publicKeyHex: string; name: string };
  jobs: StoredJob[];
  devices: StoredDevice[];
  routes: StoredRoute[];
  offers: StoredOffer[];
  seed: StoredSeed | null;
  acknowledged: Record<string, Acknowledgement>;
  collapsed: Rung[];
  operatorRequested: Rung[];
}

export function emptyState(): FleetState {
  return {
    version: 3,
    profile: null,
    issuerSeedHex: "",
    consoleDevice: { privateKeyHex: "", publicKeyHex: "", name: "This console" },
    jobs: [],
    devices: [],
    routes: [],
    offers: [],
    seed: null,
    acknowledged: {},
    collapsed: [],
    operatorRequested: []
  };
}

export function loadState(): FleetState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<FleetState>;
    if (parsed?.version !== 3) return emptyState();
    // Merged over a fresh shape so a state written by an older build of
    // this app comes back with the new fields present and empty rather
    // than as `undefined` two components down.
    return { ...emptyState(), ...parsed };
  } catch {
    return emptyState();
  }
}

export function saveState(state: FleetState): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    // A full or blocked store is not a reason to lose the screen the
    // operator is looking at. It is a reason to say so, which the shell
    // does from `storageWritable()`.
  }
}

export function storageWritable(): boolean {
  try {
    const probe = `${STORAGE_KEY}.probe`;
    localStorage.setItem(probe, "1");
    localStorage.removeItem(probe);
    return true;
  } catch {
    return false;
  }
}

let counter = 0;

/** An id for a local record. Never a key, never on the wire. */
export function localId(prefix: string): string {
  counter += 1;
  return `${prefix}-${Date.now().toString(36)}-${counter.toString(36)}`;
}
